import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Anchor, ChevronRight, ChevronsLeft, Dices, Gift, Headphones, Home, Megaphone,
  Menu, Phone, PlayCircle, Plus, Rocket, Search, ShieldCheck, Target, Ticket,
  Trophy, Wallet, X,
} from 'lucide-react';
import Logo from '../../components/Logo';
import UserDropdown from '../../components/UserDropdown';
import AuthModal from '../../components/AuthModal';
import CallbackModal from '../../components/CallbackModal';
import GameSearch from '../../components/GameSearch';
import GameModal from '../../components/GameModal';
import GameChoiceModal from '../../components/GameChoiceModal';
import ZumrutFooter from './ZumrutFooter';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useGameLauncher } from '../../hooks/useGameLauncher';
import { buildLiveChatConfig, openLiveChat } from '../../components/liveChatConfig';
import { useHiddenCategories } from '../../lib/gameCategories';
import {
  bottomNavIcon, pathOf, splitBottomNav, useBottomNavItems,
} from '../../lib/bottomNav';
import { useTopNavLinks } from '../../lib/topNav';
import { renderSettingText } from '../../lib/textTemplate';
import './zumrut.css';
import { adminShortcutVisible } from '../../lib/adminDomain';
// Panelden yazılan CSS'in tutunacağı sabit kimlikler (bkz. lib/domHooks.js):
// kimlik şemaya gömülmez, ortak sabitten okunur.
import { SHELL_IDS } from '../../lib/domHooks';

// Sol menü referans arayüzdeki gibi başlıklı gruplara ayrılır. `group` alanı
// maddenin hangi başlığın altına gireceğini söyler; başlıksız grup (null) menü
// tepesinde, kampanya düğmesinin hemen altında durur.
const NAV_ITEMS = [
  { to: '/', label: 'Anasayfa', icon: Home, end: true, group: null },
  { to: '/spor', label: 'Spor Bahisleri', icon: Trophy, sports: true, group: 'Spor' },
  { to: '/slotlar', label: 'Slot', icon: Dices, category: 'slot', group: 'Casino' },
  { to: '/canli-casino', label: 'Canlı Casino', icon: PlayCircle, category: 'live-casino', group: 'Casino' },
  { to: '/crash', label: 'Hızlı Oyunlar', icon: Rocket, category: 'crash', group: 'Casino' },
  { to: '/promosyonlar', label: 'Bonuslar', icon: Gift, group: 'Etkinlikler' },
  // Çark ve çapa kategori değildir; kendi bayrakları açıkken görünürler ve
  // panelden footer menüsüne de eklenmişlerse buradaki kopya düşer (aşağıda).
  { to: '/cark', label: 'Şans Çarkı', icon: Target, flag: 'wheel_enabled', optional: true, group: 'Etkinlikler' },
  { to: '/capa', label: 'Çapa', icon: Anchor, flag: 'anchor_enabled', optional: true, group: 'Etkinlikler' },
  // Çekiliş kategori değildir; yayında bir çekiliş varken görünür
  // (``raffle_active`` çalışma anındaki durumdur, bkz. CLAUDE.md).
  { to: '/cekilis', label: 'Çekiliş', icon: Ticket, flag: 'raffle_active', optional: true, group: 'Etkinlikler' },
  { to: '/etkinlik', label: 'Tahmin', icon: Target, flag: 'prediction_active', optional: true, group: 'Etkinlikler' },
];

// Grup başlıklarının menüdeki sırası. Listede olmayan bir grup sona eklenir.
const GROUP_ORDER = [null, 'Spor', 'Casino', 'Etkinlikler'];

const ZumrutLayout = ({ children }) => {
  const { user, balance, bonusBalance } = useAuth();
  const settings = useSettings();
  // Panel başka bir alan adına taşındıysa kısayol çizilmez: adresi
  // bilmediğimiz için bağlantı kurulamaz ve panelin varlığını
  // duyurmanın da anlamı yoktur (bkz. lib/adminDomain.js).
  const showAdminLink = adminShortcutVisible(settings);
  const nav = useNavigate();
  const location = useLocation();
  // Üstteki hızlı arama kutusundan seçilen oyun doğrudan açılabilsin diye
  // kabuk kendi başlatıcısını taşır.
  const launcher = useGameLauncher();

  // İki ayrı durum: `mini` masaüstünde menüyü ikon şeridine daraltır,
  // `drawerOpen` mobilde çekmeceyi açar.
  const [mini, setMini] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [noticeClosed, setNoticeClosed] = useState(false);
  const hiddenCats = useHiddenCategories();

  const callbackEnabled = settings.callback_enabled !== false;
  // Menüdeki "Canlı Destek" düğmesi widget'ın kendisiyle aynı koşula bağlıdır:
  // panelden kapatıldığında (ya da lisans girilmediğinde) widget hiç yüklenmez.
  const liveChatEnabled = useMemo(
    () => Boolean(buildLiveChatConfig(settings, user)),
    [settings, user],
  );

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('reset_token')) {
      setAuthTab('reset');
      setAuthOpen(true);
    }
  }, []);

  useEffect(() => {
    if (location.state?.auth !== 'login' || user) return;
    setAuthTab('login');
    setAuthOpen(true);
    nav(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, nav, user]);

  // Sayfa değişince mobil çekmece kapanır.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Çekmece açıkken arkadaki sayfa kaymasın.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [drawerOpen]);

  // Kategorilerin ardındaki kısayollar footer menüsünden gelir; kapalı bölüme
  // (spor, kategori, şans çarkı, çapa) giden bağlantıları hook'un kendisi ayıklar.
  const extraNav = useTopNavLinks();

  // Aynı adres hem kabuk listesinde hem panelde işaretliyse iki kez çizilirdi;
  // yöneticinin yazdığı etiket kazanır, kabuğun kopyası düşer.
  const shortcutPaths = useMemo(() => new Set(
    extraNav.filter((item) => !item.external).map((item) => pathOf(item.to)),
  ), [extraNav]);

  const visibleNav = useMemo(() => NAV_ITEMS.filter((item) => {
    if (item.sports) return settings.settingsLoaded && settings.sports_enabled !== false;
    if (item.optional && shortcutPaths.has(item.to)) return false;
    // Bayraklar tek tek okunur: ``settings`` nesnesinin tamamını bağımlılığa
    // koymak listeyi her renderda yeniden üretir ve ölçüm efektlerini
    // (bar taşması, aktif madde) boşuna tetiklerdi — context değeri her
    // yoklamada yeni bir nesnedir.
    if (item.flag === 'wheel_enabled') return settings.wheel_enabled === true;
    if (item.flag === 'anchor_enabled') return settings.anchor_enabled === true;
    if (item.flag === 'raffle_active') return settings.raffle_active === true;
    if (item.flag === 'prediction_active') return settings.prediction_active === true;
    return !item.category || !hiddenCats.includes(item.category);
  }), [
    hiddenCats, shortcutPaths, settings.settingsLoaded, settings.sports_enabled,
    settings.wheel_enabled, settings.anchor_enabled, settings.raffle_active, settings.prediction_active,
  ]);

  // Menü grupları: kabuğun kendi maddeleri başlıklarına göre toplanır, panelden
  // gelen kısayollar en sona kendi başlığıyla eklenir.
  const groups = useMemo(() => {
    const byTitle = new Map();
    visibleNav.forEach((item) => {
      const key = item.group || '';
      if (!byTitle.has(key)) byTitle.set(key, { key, title: item.group, items: [] });
      byTitle.get(key).items.push(item);
    });
    const ordered = GROUP_ORDER
      .map((title) => byTitle.get(title || ''))
      .filter(Boolean);
    // Sıralamada adı geçmeyen bir grup eklenirse (yeni kategori) sona düşer.
    byTitle.forEach((group) => {
      if (!ordered.includes(group)) ordered.push(group);
    });
    if (extraNav.length > 0) {
      ordered.push({ key: 'shortcuts', title: 'Kısayollar', items: extraNav });
    }
    return ordered;
  }, [visibleNav, extraNav]);

  // Alt bardaki gezinme yuvaları panelden gelir; ortadaki giriş/para yatır
  // düğmesi listeye girmez, oturuma göre kendiliğinden değişir.
  const bottomNav = useBottomNavItems();
  const { left: bottomLeft, right: bottomRight } = splitBottomNav(bottomNav);

  const openAuth = (tab) => { setAuthTab(tab); setAuthOpen(true); };

  // Panelden gelen yuva. "menu" tipi bir adrese gitmez, sol menüyü açar —
  // mobilde çekmeceye başka giriş olmadığı için panel bu yuvayı silmemeyi
  // ayrıca hatırlatır.
  const renderBottomItem = (item, index) => {
    const Icon = bottomNavIcon(item.icon);
    const key = `${item.type}-${item.url}-${index}`;
    if (item.type === 'menu') {
      return (
        <button
          key={key}
          type="button"
          className={`zumrut-bottom-item ${drawerOpen ? 'active' : ''}`}
          onClick={() => setDrawerOpen((open) => !open)}
          aria-expanded={drawerOpen}
        >
          <Icon size={18} />
          <span>{item.label}</span>
        </button>
      );
    }
    return (
      <NavLink
        key={key}
        to={item.url}
        end={item.url === '/'}
        className={({ isActive }) => `zumrut-bottom-item ${isActive ? 'active' : ''}`}
      >
        <Icon size={18} />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  // Menü satırı: kabuk maddesi de panel kısayolu da aynı görünümü kullanır,
  // fark yalnızca dış adreslerin düz bağlantı olmasıdır.
  const renderNavLink = (item) => (item.external ? (
    <a
      key={item.to}
      href={item.to}
      target={item.newTab ? '_blank' : undefined}
      rel={item.newTab ? 'noreferrer' : undefined}
      className="zumrut-side-link"
      title={item.label}
    >
      <item.icon size={17} />
      <span>{item.label}</span>
    </a>
  ) : (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      target={item.newTab ? '_blank' : undefined}
      className={({ isActive }) => `zumrut-side-link ${isActive ? 'active' : ''}`}
      title={item.label}
    >
      <item.icon size={17} />
      <span>{item.label}</span>
      <ChevronRight size={14} className="zumrut-side-link-arrow" />
    </NavLink>
  ));

  // Duyuru bandı panelde tanımlı üst bilgi metninden beslenir.
  const announcement = renderSettingText(settings.top_bar_message || '', settings).trim();

  return (
    <div className="zumrut-shell">
      <aside className={`zumrut-side ${mini ? 'is-mini' : ''} ${drawerOpen ? 'is-open' : ''}`}>
        <div className="zumrut-side-top">
          <button
            type="button"
            className="zumrut-btn zumrut-btn-gold zumrut-side-cta"
            onClick={() => nav('/promosyonlar')}
            title="Promosyonlar"
          >
            <Gift size={18} />
            <span className="zumrut-side-cta-text">Promosyonlar</span>
          </button>
          <button
            type="button"
            className="zumrut-side-toggle"
            onClick={() => (drawerOpen ? setDrawerOpen(false) : setMini(true))}
            aria-label="Menüyü daralt"
          >
            {drawerOpen ? <X size={16} /> : <ChevronsLeft size={16} />}
          </button>
        </div>

        <div className="zumrut-side-scroll">
          {groups.map((group, index) => (
            <div key={group.key}>
              {index > 0 && <div className="zumrut-side-rule" />}
              {group.title && (
                <div className="zumrut-side-group-title">{group.title}</div>
              )}
              {group.items.map(renderNavLink)}
            </div>
          ))}

          {user?.is_admin && showAdminLink && (
            <>
              <div className="zumrut-side-rule" />
              <NavLink to="/admin" className="zumrut-side-link" title="Yönetim Paneli">
                <ShieldCheck size={17} />
                <span>Yönetim Paneli</span>
              </NavLink>
            </>
          )}
        </div>

        {/* İkisi de kapalıysa menü dibinde boş bir şerit kalmasın. */}
        {(callbackEnabled || liveChatEnabled) && (
          <div className="zumrut-side-foot">
            {callbackEnabled && (
              <button
                type="button"
                className="zumrut-side-chip"
                onClick={() => setCallbackOpen(true)}
                title="Beni Ara"
              >
                <Phone size={16} />
                <span>Beni Ara</span>
              </button>
            )}
            {liveChatEnabled && (
              <button
                type="button"
                className="zumrut-side-chip"
                onClick={() => openLiveChat()}
                title="Canlı Destek"
              >
                <Headphones size={16} />
                <span>Canlı Destek</span>
              </button>
            )}
          </div>
        )}
      </aside>

      {drawerOpen && (
        <div
          className="zumrut-side-backdrop"
          role="presentation"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <div className={`zumrut-main ${mini ? 'is-wide' : ''}`}>
        <header id={SHELL_IDS.header} className="zumrut-header">
          {/* Masaüstünde menü daraltılmışsa aynı düğme onu geri açar; mobilde
              çekmecenin tek girişidir. */}
          <button
            type="button"
            className="zumrut-header-burger"
            onClick={() => setDrawerOpen(true)}
            aria-label="Menüyü aç"
          >
            <Menu size={18} />
          </button>
          {mini && (
            <button
              type="button"
              className="zumrut-side-toggle"
              onClick={() => setMini(false)}
              aria-label="Menüyü genişlet"
            >
              <Menu size={16} />
            </button>
          )}

          <Logo />

          <div className="zumrut-header-links">
            <button type="button" className="zumrut-header-link" onClick={() => setSearchOpen(true)}>
              <Search size={16} /> Hızlı Ara
            </button>
            <button
              type="button"
              className="zumrut-header-link is-accent"
              onClick={() => nav('/promosyonlar')}
            >
              <Gift size={16} /> Bonus Talep
            </button>
            <button
              type="button"
              className="zumrut-header-link"
              onClick={() => nav('/promosyon-kodu')}
            >
              <Ticket size={16} /> Promo Kod
            </button>
          </div>

          <div className="zumrut-header-actions">
            {user ? (
              <>
                <button
                  type="button"
                  className="zumrut-btn zumrut-btn-gold zumrut-btn-deposit"
                  onClick={() => nav('/hesap/para-yatir')}
                  aria-label="Para Yatır"
                >
                  <Plus size={16} />
                  <span className="zumrut-btn-text">Para Yatır</span>
                </button>
                {/* Bakiye mobilde de görünür; dar ekranda etiketi CSS gizler. */}
                <div className="zumrut-balance">
                  <Wallet size={16} style={{ color: 'var(--pusu-gold)' }} />
                  <span className="zumrut-balance-body">
                    <span className="zumrut-balance-label">Bakiye</span>
                    <span className="zumrut-balance-value">{Number(balance).toFixed(2)} ₺</span>
                  </span>
                </div>
                {/* Bonus kutusu yalnızca geniş ekranda; görünürlüğü CSS yönetir. */}
                <div className="zumrut-balance zumrut-balance-bonus">
                  <Gift size={16} style={{ color: 'var(--pusu-gold)' }} />
                  <span className="zumrut-balance-body">
                    <span className="zumrut-balance-label">Bonus</span>
                    <span className="zumrut-balance-value">{Number(bonusBalance).toFixed(2)} ₺</span>
                  </span>
                </div>
                <UserDropdown />
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="zumrut-btn zumrut-btn-ghost zumrut-btn-login"
                  onClick={() => openAuth('login')}
                >
                  Giriş
                </button>
                {settings.registration_open !== false && (
                  <button
                    type="button"
                    className="zumrut-btn zumrut-btn-gold"
                    onClick={() => openAuth('register')}
                  >
                    Kayıt
                  </button>
                )}
              </>
            )}
          </div>
        </header>

        {announcement && !noticeClosed && (
          <div className="zumrut-notice">
            <span className="zumrut-notice-icon"><Megaphone size={15} /></span>
            <p className="zumrut-notice-text">{announcement}</p>
            <button
              type="button"
              className="zumrut-notice-close"
              onClick={() => setNoticeClosed(true)}
              aria-label="Duyuruyu kapat"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div id={SHELL_IDS.main} className="zumrut-content">{children}</div>

        <ZumrutFooter />
      </div>

      {/* Alt bar beş yuvadır: panelden gelen dört yuva ile ortadaki sabit
          giriş/para yatır düğmesi. */}
      <nav id={SHELL_IDS.bottomBar} className="app-bottom-bar zumrut-bottom-bar" aria-label="Alt menü">
        {bottomLeft.map(renderBottomItem)}
        {user ? (
          <NavLink to="/hesap/para-yatir" className="zumrut-bottom-item is-wallet">
            <Wallet size={20} />
            <span>Para Yatır</span>
          </NavLink>
        ) : (
          <button
            type="button"
            className="zumrut-bottom-item is-wallet"
            onClick={() => openAuth('login')}
          >
            <Wallet size={20} />
            <span>Giriş Yap</span>
          </button>
        )}
        {bottomRight.map(renderBottomItem)}
      </nav>

      {searchOpen && (
        <div
          className="fixed inset-0 z-[70] flex justify-center px-4 pt-24"
          style={{ background: 'rgba(0, 12, 10, 0.78)' }}
          onClick={() => setSearchOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-2xl"
            onClick={(event) => event.stopPropagation()}
            role="presentation"
          >
            <GameSearch onPlay={(game) => { setSearchOpen(false); launcher.play(game); }} />
          </div>
        </div>
      )}

      <GameChoiceModal
        game={launcher.choice}
        user={launcher.user}
        balance={launcher.balance}
        bonusBalance={launcher.bonusBalance}
        demoAvailable={launcher.demoAvailable}
        onClose={launcher.closeChoice}
        onReal={launcher.startReal}
        onDemo={launcher.startDemo}
        onLogin={() => launcher.openAuth('login')}
        onRegister={() => launcher.openAuth('register')}
      />
      {launcher.activeGame && (
        <GameModal
          key={`${launcher.activeGame.id}-${launcher.mode}`}
          game={launcher.activeGame}
          mode={launcher.mode}
          onClose={launcher.closeGame}
        />
      )}

      <AuthModal
        open={authOpen || !!launcher.authTab}
        onClose={() => { setAuthOpen(false); launcher.setAuthTab(null); }}
        defaultTab={launcher.authTab || authTab}
        registrationOpen={settings.registration_open !== false}
      />
      <CallbackModal open={callbackEnabled && callbackOpen} onClose={() => setCallbackOpen(false)} />
    </div>
  );
};

export default ZumrutLayout;
